import type { AppProps } from 'next/app'
import Head from 'next/head'
import { Global } from '@mantine/core'

import { config } from '@/config'
import { ChainProvider } from '@/contexts/chain'
import { ColorSchemeProvider } from '@/contexts/color-scheme'
import MantineProvider from '@/contexts/mantine'
import NotificationsProvider from '@/contexts/notifications'
import QueryClientProvider from '@/contexts/query'
import QueryParamsProvider from '@/contexts/query'
import { RollbarProvider } from '@/contexts/rollbar'
import { TopBannerProvider } from '@/contexts/top-banner'
import { PlausibleProvider } from '@/contexts/plausible'
import { GlobalLayout, GlobalLayoutProvider } from '@/page-components/GlobalLayout'
import { env } from '@/utils'
import { SelectedDenomProvider } from '@/contexts/denom'

function App({ Component, pageProps }: AppProps) {
  const favicon: Record<string, string> = {
    development: '/favicon-local.png?v=1',
    staging: '/favicon-staging.png?v=1',
    production: '/favicon.png?v=2'
  }

  return (
    <>
      <Head>
        <title>{`${config.app.title} | ${config.app.tagline}`}</title>
        <meta name="viewport" content="minimum-scale=1, initial-scale=1, width=device-width" />
        <meta
          name="description"
          content="Stride is a liquid staking protocol for the Cosmos ecosystem. Stake ATOM, OSMO, and other Cosmos tokens while earning DeFi rewards. Built on Cosmos Hub with IBC integration."
        />

        <meta property="og:type" content="website" />
        <meta content="Stride Protocol | Cosmos Liquid Staking & DeFi Platform" property="og:title" />
        <meta
          property="og:description"
          content="Stride is a liquid staking protocol for the Cosmos ecosystem. Stake ATOM, OSMO, and other Cosmos tokens while earning DeFi rewards. Built on Cosmos Hub with IBC integration."
        />
        <meta content="/stride-og.png?v=3" property="og:image" />
        <meta content="https://app.stride.zone" property="og:url" />

        <meta content="Stride Protocol | Cosmos Liquid Staking & DeFi Platform" property="twitter:title" />
        <meta
          property="twitter:description"
          content="Stride is a liquid staking protocol for the Cosmos ecosystem. Stake ATOM, OSMO, and other Cosmos tokens while earning DeFi rewards. Built on Cosmos Hub with IBC integration."
        />
        {/* `twitter:image` only works with absolute urls */}
        <meta content="https://app.stride.zone/stride-og.png?v=3" name="twitter:image" />
        <meta property="twitter:creator" content="@stride_zone" />
        <meta property="twitter:site" content="@stride_zone" />
        <meta content="summary_large_image" name="twitter:card" />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Stride Protocol" />
        <meta
          name="keywords"
          content="liquid staking, cosmos, DeFi, STRD token, cosmos hub, IBC, staking protocol, ATOM staking, cryptocurrency, blockchain"
        />

        <link href={favicon[env('STRIDE_ENV', 'production')]} rel="shortcut icon" type="image/x-icon" />
        <link href="/webclip.png?v=2" rel="apple-touch-icon" />
        <link rel="canonical" href="https://app.stride.zone/" />

        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'SoftwareApplication',
            name: 'Stride Protocol',
            applicationCategory: 'DeFi Platform',
            description:
              'Liquid staking protocol for the Cosmos ecosystem enabling users to stake ATOM and other tokens while maintaining liquidity',
            url: 'https://app.stride.zone',
            operatingSystem: 'Web',
            offers: {
              '@type': 'Offer',
              price: '0',
              priceCurrency: 'USD'
            },
            keywords:
              'liquid staking, cosmos, DeFi, STRD token, cosmos hub, IBC, staking protocol, ATOM staking, cryptocurrency, blockchain',
            applicationSubCategory: 'Cryptocurrency Platform'
          })}
        </script>
      </Head>

      <PlausibleProvider>
        <ColorSchemeProvider>
          <MantineProvider>
            {/* We are placing this here so we can style our fallback UI when our app crashes. */}
            {/* For awareness, this strongly assumes that both providers above won't crash. */}
            <RollbarProvider>
              <QueryClientProvider>
                <QueryParamsProvider>
                  <NotificationsProvider>
                    <TopBannerProvider>
                      <ChainProvider>
                        <SelectedDenomProvider>
                          <GlobalLayoutProvider>
                            <GlobalLayout>
                              <Component {...pageProps} />
                            </GlobalLayout>
                          </GlobalLayoutProvider>
                        </SelectedDenomProvider>
                      </ChainProvider>
                    </TopBannerProvider>
                  </NotificationsProvider>
                </QueryParamsProvider>
              </QueryClientProvider>
            </RollbarProvider>

            <Global
              styles={[
                {
                  '*, *::before, *::after': {
                    boxSizing: 'border-box'
                  },

                  body: {
                    background: '#FFEDF9',
                    overflowX: 'hidden'
                  }
                },
                {
                  '@font-face': {
                    fontFamily: 'PP Telegraf',
                    fontWeight: 400,
                    src: 'url("/fonts/PPTelegraf-Regular.ttf")'
                  }
                },
                {
                  '@font-face': {
                    fontFamily: 'PP Telegraf',
                    fontWeight: 600,
                    src: 'url("/fonts/PPTelegraf-Semibold.ttf")'
                  }
                },
                {
                  '@font-face': {
                    fontFamily: 'PP Telegraf',
                    fontWeight: 800,
                    src: 'url("/fonts/PPTelegraf-Ultrabold.otf")'
                  }
                }
              ]}
            />
          </MantineProvider>
        </ColorSchemeProvider>
      </PlausibleProvider>
    </>
  )
}

export default App
